import React, {useState, useContext} from 'react'
import {useTranslation} from 'react-i18next'

import {
  StatsContainer,
  EditableCard,
  DepartmentSection,
  InputRow,
  StatsCard,
  StatsRow,
  StatsGrid,
  StatsGridFourColumns,
  StatItem,
  SectionTitle,
  EditableSectionTitle,
  DepartmentTitle,
  FieldLabel,
  SubmitButtonContainer,
} from './EndOfDayModal.styled'

import {SideModal} from '~/components/general/modal/SideModal'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Loading} from '~/components/general/loading/Loading'
import {Input} from '~/components/general/input/Input'
import {TextArea} from '~/components/general/textArea/TextArea'
import {Button} from '~/components/general/button/Button'
import {useGetEndOfDayData, useSubmitEndOfDayReport} from '~/api/endOfDay'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

type EndOfDayModalProps = {
  isVisible: boolean
  onClose: () => void
  facilityId?: string
}

type EditableData = {
  clinicalIncidentsED: string
  clinicalIncidentsOther: string
  healthSafetyIncidents: string
  otherDetails: string
}

const EndOfDayModal: React.FC<EndOfDayModalProps> = ({
  isVisible,
  onClose,
  facilityId,
}) => {
  const {t} = useTranslation<string>()
  const {addNotification} = useContext(NotificationContext)
  const {
    data: endOfDayData,
    isLoading,
    error,
  } = useGetEndOfDayData(facilityId || '', {
    enabled: isVisible && !!facilityId,
  })

  // Initialize editable data for each department
  const [editableData, setEditableData] = useState<
    Record<string, EditableData>
  >({})

  const submitEndOfDayReport = useSubmitEndOfDayReport(facilityId || '')

  const updateEditableData = (
    department: string,
    field: keyof EditableData,
    value: string,
  ) => {
    setEditableData((prev) => ({
      ...prev,
      [department]: {
        ...prev[department],
        [field]: value,
      },
    }))
  }

  const getEditableValue = (
    department: string,
    field: keyof EditableData,
  ): string => editableData[department]?.[field] || ''

  // prettier-ignore
  const generateReadOnlyMessage = (): string => {
    if (!endOfDayData) return ''

    return endOfDayData
      .map((dept) => {
        const totalGender =
          dept.maleCount + dept.femaleCount + dept.undefinedGenderCount

        const {scheduledCount} = dept

        return `
          <table style="font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #2c2c2c; width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <tr>
              <td colspan="2" style="font-size: 16px; font-weight: bold; color: #1a73e8; padding-bottom: 6px; border-bottom: 2px solid #eee;">
                ${dept.department}
              </td>
            </tr>

            <tr><td style="padding: 6px 0; font-weight: 600; color: #444;">${t('Nurse')}:</td><td style="padding: 6px 0; color: #5f6f81;">${dept.nurse}</td></tr>

            <tr><td colspan="2" style="padding-top: 14px; font-weight: bold; color: #444;">${t('Number of workers...')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Scheduled')}:</td><td style="padding: 4px 0; color: #5f6f81;">${(scheduledCount)}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Screened')}:</td><td style="padding: 4px 0; color: #5f6f81;">${totalGender}</td></tr>

            <tr><td colspan="2" style="padding-top: 14px; font-weight: bold; color: #444;">${t('Number of appointments...')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Cancelled by Nurse')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.cancelledCountInternal}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Cancelled by Worker')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.cancelledCountExternal}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Rescheduled')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.rescheduledCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Did Not Attend')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.dnaCount}</td></tr>

            <tr><td colspan="2" style="padding-top: 14px; font-weight: bold; color: #444;">${t('Gender Distribution')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Male')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.maleCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Female')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.femaleCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Undefined')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.undefinedGenderCount}</td></tr>
            <tr><td style="padding: 4px 0;"><strong>${t('Total')}:</strong></td><td style="padding: 4px 0; color: #5f6f81;">${totalGender}</td></tr>

            <tr><td colspan="2" style="padding-top: 14px; font-weight: bold; color: #444;">${t('Plan')}</td></tr>

            <tr><td colspan="2" style="padding-top: 6px; font-style: italic; color: #444;">${t('Lifestyle advice only')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Yes')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.lifestyleOnlyYesCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('No')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.lifestyleOnlyNoOrNullCount}</td></tr>

            <tr><td colspan="2" style="padding-top: 6px; font-style: italic; color: #444;">${t('Lifestyle advice and future GP visit')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Yes')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.lifestyleAndGPVisitYesCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('No')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.lifestyleAndGPVisitNoOrNullCount}</td></tr>

            <tr><td colspan="2" style="padding-top: 6px; font-style: italic; color: #444;">${t('Recommend visit to GP (within 3-4 weeks)')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Own GP')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.gpOwnCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Centric')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.gpCentricCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('No')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.gpNoOrNullCount}</td></tr>

            <tr><td colspan="2" style="padding-top: 6px; font-style: italic; color: #444;">${t('Recommend visit to Emergency Department')}</td></tr>
            <tr><td style="padding: 4px 0;">${t('Yes')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.edYesCount}</td></tr>
            <tr><td style="padding: 4px 0;">${t('No')}:</td><td style="padding: 4px 0; color: #5f6f81;">${dept.edNoOrNullCount}</td></tr>
          </table>
        `.trim()
      })
      .join('<div style="border-top: 1px solid #ddd; margin: 30px 0;"></div>')
  }

  const generateWritableMessage = (): string => {
    if (!endOfDayData) return ''

    return endOfDayData
      .map((dept) => {
        const deptData = editableData[dept.department]

        if (!deptData) {
          return `
          <table style="font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #2c2c2c; width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <tr>
              <td style="font-size: 16px; font-weight: bold; color: #1a73e8; padding-bottom: 4px;">
                ${dept.department}
              </td>
            </tr>
            <tr>
              <td style="padding: 6px 0; color: #777;">${t(
                'No additional information provided',
              )}.</td>
            </tr>
          </table>
        `.trim()
        }

        return `
        <table style="font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #2c2c2c; width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <tr>
            <td style="font-size: 16px; font-weight: bold; color: #1a73e8; padding-bottom: 6px; border-bottom: 2px solid #eee;">
              ${dept.department}
            </td>
          </tr>

          <tr><td style="padding-top: 12px; font-weight: 600; color: #444;">${t(
            'Number of clinical incidents - recommended to attend ED',
          )}</td></tr>
          <tr><td style="padding-bottom: 8px; color: #5f6f81;">${
            deptData.clinicalIncidentsED || '0'
          }</td></tr>

          <tr><td style="padding-top: 8px; font-weight: 600; color: #444;">${t(
            'Number of clinical incidents - other',
          )}</td></tr>
          <tr><td style="padding-bottom: 8px; color: #5f6f81;">${
            deptData.clinicalIncidentsOther || '0'
          }</td></tr>

          <tr><td style="padding-top: 8px; font-weight: 600; color: #444;">${t(
            'Number of Health & Safety incidents',
          )}</td></tr>
          <tr><td style="padding-bottom: 8px; color: #5f6f81;">${
            deptData.healthSafetyIncidents || '0'
          }</td></tr>

          <tr><td style="padding-top: 8px; font-weight: 600; color: #444;">${t(
            'Any other details relevant to the screening day',
          )}</td></tr>
          <tr><td style="padding-bottom: 8px; color: #5f6f81;">${
            deptData.otherDetails ||
            `<em style="color: #999;">${t('None')}</em>`
          }</td></tr>
        </table>
      `.trim()
      })
      .join('<div style="border-top: 1px solid #ddd; margin: 30px 0;"></div>')
  }

  const validateAppointmentBalance = (): boolean => {
    if (!endOfDayData) return true

    return !endOfDayData.some((dept) => {
      const {scheduledCount} = dept
      const screenedCount =
        dept.maleCount + dept.femaleCount + dept.undefinedGenderCount
      const cancelledByNurse = dept.cancelledCountInternal
      const cancelledByWorker = dept.cancelledCountExternal
      const didNotAttend = dept.dnaCount

      const totalAccountedFor =
        screenedCount + cancelledByNurse + cancelledByWorker + didNotAttend

      return scheduledCount > totalAccountedFor
    })
  }

  const validateOtherDetailsRequired = (): boolean => {
    if (!endOfDayData) return true

    return endOfDayData.every((dept) => {
      const otherDetails = getEditableValue(dept.department, 'otherDetails')
      return otherDetails.trim().length > 0
    })
  }

  const handleSubmit = async () => {
    if (!facilityId) return

    if (!validateOtherDetailsRequired()) {
      window.alert(
        t(
          'You cannot proceed until you enter relevant information in the "Any other details relevant to the screening day" field.',
        ),
      )
      return
    }

    // Validate appointment balance
    if (!validateAppointmentBalance()) {
      const userConfirmed = window.confirm(
        t(
          "There is still an appointment that is in 'Pending' status.  If you want to correct this status please select 'Cancel' or if you want to submit like this, select 'OK'",
        ),
      )

      if (!userConfirmed) {
        return // User chose to cancel, exit without submitting
      }
    }

    const readOnlyMessage = generateReadOnlyMessage()
    const writableMessage = generateWritableMessage()

    if (
      window.confirm(
        t('Are you sure you want to submit the End of Day report?'),
      )
    ) {
      try {
        await submitEndOfDayReport.mutateAsync({
          readOnlyMessage,
          writableMessage,
        })

        addNotification(
          t('End of Day report submitted successfully'),
          NotificationType.Success,
        )

        onClose()
      } catch (submitError) {
        addNotification(
          `${t('Error submitting End of Day report')}\n${
            submitError instanceof Error
              ? submitError.message
              : t('Unknown error occurred')
          }`,
          NotificationType.Error,
        )
      }
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {t('End of the Day Report')}
          </Typography>
        </Container>
      }>
      <StatsContainer>
        {isLoading && <Loading />}

        {error && (
          <Typography size="md" color="error">
            {t('Error loading end of day data')}
          </Typography>
        )}

        {!facilityId && (
          <Typography size="md" color="secondary">
            {t('Please select a facility to view end of day data')}
          </Typography>
        )}

        {facilityId && endOfDayData && endOfDayData.length > 0 ? (
          <>
            {endOfDayData.map((departmentData, index) => (
              <StatsCard key={index}>
                <Typography size="md" weight="medium" color="primary">
                  {departmentData.department}
                </Typography>

                <StatsRow>
                  <Typography size="sm" color="secondary">
                    {t('Nurse')}: {departmentData.nurse}
                  </Typography>
                </StatsRow>

                <StatsRow>
                  <Typography size="sm" color="secondary">
                    {t('Number of workers scheduled')}:{' '}
                    {departmentData.scheduledCount}
                  </Typography>
                </StatsRow>

                <StatsRow>
                  <Typography size="sm" color="secondary">
                    {t('Number of workers screened')}:{' '}
                    {departmentData.maleCount +
                      departmentData.femaleCount +
                      departmentData.undefinedGenderCount}
                  </Typography>
                </StatsRow>

                <SectionTitle size="sm" weight="medium">
                  {t('Gender Distribution')}:
                </SectionTitle>
                <StatsGridFourColumns>
                  <StatItem>
                    <Typography size="sm">{t('Male')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.maleCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('Female')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.femaleCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('Undefined')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.undefinedGenderCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('Total')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.maleCount +
                        departmentData.femaleCount +
                        departmentData.undefinedGenderCount}
                    </Typography>
                  </StatItem>
                </StatsGridFourColumns>

                <SectionTitle size="sm" weight="medium">
                  {t('Recommend visit to GP')}:
                </SectionTitle>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">{t('Own GP')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.gpOwnCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('Centric')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.gpCentricCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('No')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.gpNoOrNullCount}
                    </Typography>
                  </StatItem>
                </StatsGrid>

                <SectionTitle size="sm" weight="medium">
                  {t('Recommend visit to Emergency Department')}:
                </SectionTitle>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">{t('Yes')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.edYesCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('No')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.edNoOrNullCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <div />
                  </StatItem>
                </StatsGrid>

                <SectionTitle size="sm" weight="medium">
                  {t('Lifestyle Advice Only')}:
                </SectionTitle>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">{t('Yes')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.lifestyleOnlyYesCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('No')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.lifestyleOnlyNoOrNullCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <div />
                  </StatItem>
                </StatsGrid>

                <SectionTitle size="sm" weight="medium">
                  {t('Lifestyle Advice and Future GP Visit')}:
                </SectionTitle>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">{t('Yes')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.lifestyleAndGPVisitYesCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('No')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.lifestyleAndGPVisitNoOrNullCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <div />
                  </StatItem>
                </StatsGrid>

                <SectionTitle size="sm" weight="medium">
                  {t('Appointment Status')}:
                </SectionTitle>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">
                      {t('Cancelled by Nurse')}:
                    </Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.cancelledCountInternal}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">
                      {t('Cancelled by Worker')}:
                    </Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.cancelledCountExternal}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <Typography size="sm">{t('Rescheduled')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.rescheduledCount}
                    </Typography>
                  </StatItem>
                </StatsGrid>
                <StatsGrid>
                  <StatItem>
                    <Typography size="sm">{t('Did Not Attend')}:</Typography>
                    <Typography size="sm" weight="medium">
                      {departmentData.dnaCount}
                    </Typography>
                  </StatItem>
                  <StatItem>
                    <div />
                  </StatItem>
                  <StatItem>
                    <div />
                  </StatItem>
                </StatsGrid>
              </StatsCard>
            ))}

            {/* Editable Section */}
            <EditableCard>
              <EditableSectionTitle size="md" weight="medium">
                {t('Additional Information')}
              </EditableSectionTitle>

              {endOfDayData?.map((departmentData, index) => (
                <DepartmentSection key={index}>
                  <DepartmentTitle size="sm" weight="medium" color="primary">
                    {departmentData.department}
                  </DepartmentTitle>

                  <InputRow>
                    <FieldLabel size="sm">
                      {t(
                        'Number of clinical incidents - recommended to attend ED',
                      )}
                      :
                    </FieldLabel>
                    <Input
                      type="number"
                      value={getEditableValue(
                        departmentData.department,
                        'clinicalIncidentsED',
                      )}
                      onChange={(e) =>
                        updateEditableData(
                          departmentData.department,
                          'clinicalIncidentsED',
                          e.target.value,
                        )
                      }
                      placeholder="0"
                    />
                  </InputRow>

                  <InputRow>
                    <FieldLabel size="sm">
                      {t('Number of clinical incidents - other')}:
                    </FieldLabel>
                    <Input
                      type="number"
                      value={getEditableValue(
                        departmentData.department,
                        'clinicalIncidentsOther',
                      )}
                      onChange={(e) =>
                        updateEditableData(
                          departmentData.department,
                          'clinicalIncidentsOther',
                          e.target.value,
                        )
                      }
                      placeholder="0"
                    />
                  </InputRow>

                  <InputRow>
                    <FieldLabel size="sm">
                      {t('Number of Health & Safety incidents')}:
                    </FieldLabel>
                    <Input
                      type="number"
                      value={getEditableValue(
                        departmentData.department,
                        'healthSafetyIncidents',
                      )}
                      onChange={(e) =>
                        updateEditableData(
                          departmentData.department,
                          'healthSafetyIncidents',
                          e.target.value,
                        )
                      }
                      placeholder="0"
                    />
                  </InputRow>

                  <InputRow>
                    <FieldLabel
                      size="sm"
                      inline
                      required={
                        !getEditableValue(
                          departmentData.department,
                          'otherDetails',
                        ).trim()
                      }>
                      {t('Any other details relevant to the screening day')}:
                    </FieldLabel>
                    <TextArea
                      value={getEditableValue(
                        departmentData.department,
                        'otherDetails',
                      )}
                      onChange={(e) =>
                        updateEditableData(
                          departmentData.department,
                          'otherDetails',
                          e.target.value,
                        )
                      }
                      placeholder={t('Enter any additional details...')}
                      rows={3}
                      required={
                        !getEditableValue(
                          departmentData.department,
                          'otherDetails',
                        ).trim()
                      }
                    />
                  </InputRow>
                </DepartmentSection>
              ))}
            </EditableCard>
          </>
        ) : (
          !isLoading &&
          facilityId && (
            <Typography size="md" color="secondary">
              {t('No end of day data available')}
            </Typography>
          )
        )}
      </StatsContainer>

      {/* Submit Button - Moved outside of StatsContainer to be at the bottom */}
      {facilityId && endOfDayData && endOfDayData.length > 0 && (
        <SubmitButtonContainer>
          <Button
            onClick={handleSubmit}
            disabled={submitEndOfDayReport.isPending}>
            {submitEndOfDayReport.isPending
              ? t('Submitting...')
              : t('Submit End of Day Report')}
          </Button>
        </SubmitButtonContainer>
      )}
    </SideModal>
  )
}

export default EndOfDayModal
